/**
 * AWS Regions
 */
const AWS_REGIONS = [
  { 'id': 'us-east-2', 'label': 'US East (Ohio)', },
  { 'id': 'us-east-1', 'label': 'US East (N. Virginia)', },
  { 'id': 'us-west-1', 'label': 'US West (N. California)', },
  { 'id': 'us-west-2', 'label': 'US West (Oregon)', },
  { 'id': 'us-gov-east-1', 'label': 'AWS GovCloud (US-East)', },
  { 'id': 'us-gov-west-1', 'label': 'AWS GovCloud (US-West)', },
  { 'id': 'ca-central-1', 'label': 'Canada (Central)', },
  { 'id': 'ca-west-1', 'label': 'Canada West (Calgary)', },
  { 'id': 'af-south-1', 'label': 'Africa (Cape Town)', },
  { 'id': 'ap-east-1', 'label': 'Asia Pacific (Hong Kong)', },
  { 'id': 'ap-south-2', 'label': 'Asia Pacific (Hyderabad)', },
  { 'id': 'ap-southeast-3', 'label': 'Asia Pacific (Jakarta)', },
  { 'id': 'ap-southeast-4', 'label': 'Asia Pacific (Melbourne)', },
  { 'id': 'ap-south-1', 'label': 'Asia Pacific (Mumbai)', },
  { 'id': 'ap-northeast-3', 'label': 'Asia Pacific (Osaka)', },
  { 'id': 'ap-northeast-2', 'label': 'Asia Pacific (Seoul)', },
  { 'id': 'ap-southeast-1', 'label': 'Asia Pacific (Singapore)', },
  { 'id': 'ap-southeast-2', 'label': 'Asia Pacific (Sydney)', },
  { 'id': 'ap-northeast-1', 'label': 'Asia Pacific (Tokyo)', },
  { 'id': 'eu-central-1', 'label': 'Europe (Frankfurt)', },
  { 'id': 'eu-west-1', 'label': 'Europe (Ireland)', },
  { 'id': 'eu-west-2', 'label': 'Europe (London)', },
  { 'id': 'eu-south-1', 'label': 'Europe (Milan)', },
  { 'id': 'eu-west-3', 'label': 'Europe (Paris)', },
  { 'id': 'eu-south-2', 'label': 'Europe (Spain)', },
  { 'id': 'eu-north-1', 'label': 'Europe (Stockholm)', },
  { 'id': 'eu-central-2', 'label': 'Europe (Zurich)', },
  { 'id': 'il-central-1', 'label': 'Israel (Tel Aviv)', },
  { 'id': 'me-south-1', 'label': 'Middle East (Bahrain)', },
  { 'id': 'me-central-1', 'label': 'Middle East (UAE)', },
  { 'id': 'sa-east-1', 'label': 'South America (São Paulo)', },
]

/**
 * Application Fields
 */
const AppFields = [
  { id: 'anniversary', label: 'Anniversary' },
  { id: 'business_name', label: 'Business Name' },
  { id: 'birthday', label: 'DOB' },
  { id: 'email', label: 'Email' },
  { id: 'full_name', label: 'Full Name' },
  { id: 'first_name', label: 'First Name' },
  { id: 'last_name', label: 'Last Name' },
  { id: 'phone', label: 'Phone' },
]

/**
 * Page-size options for paginated lists.
 *
 * These are the sizes the API will actually serve. It clamps any page to
 * PER_PAGE_MAX, so offering "All" promised something it could not deliver: on
 * 2026-10-21, "All" on a 20,063-contact account exhausted the server's 128 MB
 * memory limit and the list failed to load at all. Keep this in step with
 * PER_PAGE_MAX in customer-engine-api's app/helpers.php.
 */
export const PER_PAGE_MAX = 1000

/** Standard page sizes, for lists that stay small. */
export const PER_PAGE_OPTIONS = Object.freeze([10, 15, 25, 50, 100])

/** For lists that can run to thousands of rows: contacts, whitelist, blacklist, loop stats. */
export const PER_PAGE_OPTIONS_LARGE = Object.freeze([10, 15, 25, 50, 100, 250, 500, PER_PAGE_MAX])

/**
 * Custom Field Type Constants
 * 
 * These constants represent the different types of custom fields
 * that can be created and used throughout the application.
 */
export const CUSTOM_FIELD_TYPES = Object.freeze({
  STRING: 1,
  DATE: 2,
  NUMBER: 3,
})

/**
 * Human-readable labels for custom field types
 */
export const CUSTOM_FIELD_TYPE_LABELS = Object.freeze({
  [CUSTOM_FIELD_TYPES.STRING]: 'String',
  [CUSTOM_FIELD_TYPES.DATE]: 'Date',
  [CUSTOM_FIELD_TYPES.NUMBER]: 'Number',
})

/**
 * Get the default value for a custom field based on its type
 * 
 * @param {number} fieldType - The custom field type (use CUSTOM_FIELD_TYPES constants)
 * @returns {string|number} The default value for the field type
 */
export function getCustomFieldDefaultValue(fieldType) {
  switch (fieldType) {
    case CUSTOM_FIELD_TYPES.STRING:
      return ''
    case CUSTOM_FIELD_TYPES.DATE:
      return ''
    case CUSTOM_FIELD_TYPES.NUMBER:
      return 0
    default:
      return ''
  }
}

/**
 * Get the format string for a custom field based on its type
 * Used for display formatting in automation contexts
 * 
 * @param {number} fieldType - The custom field type (use CUSTOM_FIELD_TYPES constants)
 * @returns {string} The format string for the field type
 */
export function getCustomFieldFormat(fieldType) {
  switch (fieldType) {
    case CUSTOM_FIELD_TYPES.DATE:
      return 'mm/dd'
    default:
      return ''
  }
}

/**
 * Get all field types as an array suitable for dropdowns
 * 
 * @returns {Array<{value: number, label: string}>} Array of field type options
 */
export function getCustomFieldTypeOptions() {
  return [
    { value: CUSTOM_FIELD_TYPES.STRING, label: CUSTOM_FIELD_TYPE_LABELS[CUSTOM_FIELD_TYPES.STRING] },
    { value: CUSTOM_FIELD_TYPES.DATE, label: CUSTOM_FIELD_TYPE_LABELS[CUSTOM_FIELD_TYPES.DATE] },
    { value: CUSTOM_FIELD_TYPES.NUMBER, label: CUSTOM_FIELD_TYPE_LABELS[CUSTOM_FIELD_TYPES.NUMBER] },
  ]
}

// Export legacy constants
export {
  AWS_REGIONS,
  AppFields,
}
